import { Tab, TabList, TabPanel, Tabs } from 'react-aria-components';

import { LandingImage } from './LandingImage';
import styles from './MessageStates.module.css';

type AgentId =
	| 'community'
	| 'sales'
	| 'creative'
	| 'copywriter'
	| 'retention'
	| 'pr';

type Message = { role: 'agent' | 'user'; text: string; reaction?: boolean };

const AGENTS: {
	id: AgentId;
	name: string;
	role: string;
	icon: string;
	context: string;
	messages: Message[];
}[] = [
	{
		id: 'community',
		name: 'The Community Manager',
		role: 'Sorts every DM and drafts the reply',
		icon: '/landing/agent-icon-community.png',
		context:
			"You're a fast-growing supplement brand. Project Bridgeway keeps your community engaged.",
		messages: [
			{
				role: 'agent',
				text: 'You got 23 DMs overnight. I sorted them: 8 are product inquiries, 3 are collab pitches, and 12 are positive reviews. Want me to draft responses to the product inquiries?',
			},
			{ role: 'user', text: 'Yes, show me the responses.', reaction: true },
			{
				role: 'agent',
				text: 'Will do, following your FAQ guidelines. You also have 3 new comments on yesterday’s reel. Should I draft replies?',
			},
			{ role: 'user', text: 'Perfect, yes!' },
		],
	},
	{
		id: 'sales',
		name: 'The Sales Executive',
		role: 'Finds new customers and drafts the outreach',
		icon: '/landing/agent-icon-sales.png',
		context:
			"You're a B2B agency looking for design clients. Project Bridgeway finds and warms your pipeline.",
		messages: [
			{
				role: 'agent',
				text: 'Sourced 12 ideal prospects today. Pulled context from each company’s site and recent hires.',
			},
			{ role: 'user', text: 'Send the top three.', reaction: true },
			{
				role: 'agent',
				text: 'Drafts ready in your voice — personalized hook for each. Quick scan and I send.',
			},
			{ role: 'user', text: 'Looks good!' },
		],
	},
	{
		id: 'creative',
		name: 'The Creative Strategist',
		role: 'Finds trends worth posting on this week',
		icon: '/landing/agent-icon-creative.png',
		context:
			"You're a wellness coach building a content engine. Project Bridgeway tracks what’s resonating now.",
		messages: [
			{
				role: 'agent',
				text: 'Three trends spiking in your niche this week — cold plunge debate, gut-health protocol, and journaling stacks.',
			},
			{ role: 'user', text: 'Pick the strongest hook.', reaction: true },
			{
				role: 'agent',
				text: 'Cold plunge — your audience already engages with it. I’ll draft a carousel and a reel concept by tomorrow.',
			},
			{ role: 'user', text: 'Perfect.' },
		],
	},
	{
		id: 'copywriter',
		name: 'The Copywriter',
		role: 'Writes your weekly customer emails',
		icon: '/landing/agent-icon-copywriter.png',
		context:
			"You're a coffee subscription brand. Project Bridgeway writes your weekly newsletter.",
		messages: [
			{
				role: 'agent',
				text: 'This week’s draft leads with the new Ethiopia single-origin and a barista interview.',
			},
			{ role: 'user', text: 'Send it out.', reaction: true },
			{
				role: 'agent',
				text: 'Subject-line variants ready for A/B. I’ll send the winner to the full list at 7am tomorrow.',
			},
			{ role: 'user', text: 'Great.' },
		],
	},
	{
		id: 'retention',
		name: 'The Retention Specialist',
		role: "Wins back customers who've gone quiet",
		icon: '/landing/agent-icon-retention.png',
		context:
			"You're a med spa with a 90-day churn pattern. Project Bridgeway brings lapsed clients back.",
		messages: [
			{
				role: 'agent',
				text: 'Found 42 clients past their usual return window. Segmenting by last service — most are facial, then injectables.',
			},
			{ role: 'user', text: 'Start with the facial group.', reaction: true },
			{
				role: 'agent',
				text: 'Sending a warm check-in with a touch-up offer. Soft tone, opt-out is one tap.',
			},
			{ role: 'user', text: 'Yes.' },
		],
	},
	{
		id: 'pr',
		name: 'The PR Manager',
		role: 'Replies to reviews and asks for new ones',
		icon: '/landing/agent-icon-pr.png',
		context:
			"You're a salon with 4.6 stars on Google. Project Bridgeway protects your reputation.",
		messages: [
			{
				role: 'agent',
				text: 'New 5-star review on Yelp from Maya. I’ll thank her and ask if she’d repost on Instagram.',
			},
			{ role: 'user', text: 'Do it.', reaction: true },
			{
				role: 'agent',
				text: 'Also drafted a thoughtful reply to the 3-star from yesterday — acknowledging the wait, offering a callback.',
			},
			{ role: 'user', text: 'Send it.' },
		],
	},
];

const ThumbsUp = () => (
	<svg
		viewBox="0 0 18 18"
		width="14"
		height="14"
		aria-hidden="true"
		focusable="false"
	>
		<path
			d="M6 8h-2v7h2V8zm8.2-.5h-3.6l.6-2.7c.1-.3 0-.7-.2-.9l-.6-.6L7 6.5v8.5h7.4c.6 0 1.2-.5 1.3-1.1l1-4.6c.1-.7-.4-1.3-1-1.3z"
			fill="#fff"
		/>
	</svg>
);

const ChatThread = ({
	context,
	messages,
}: {
	context: string;
	messages: Message[];
}) => (
	<div className={styles.threadCard}>
		<div className={styles.contextPill}>{context}</div>
		<div className={styles.thread}>
			<div className={styles.timestamp}>
				<span className={styles.timestampDay}>Today</span>
				<span className={styles.timestampTime}>at 8:46 AM</span>
			</div>
			{messages.map((m, i) => (
				<div
					key={i}
					className={`${styles.bubbleRow} ${
						m.role === 'agent' ? styles.bubbleRowLeft : styles.bubbleRowRight
					}`}
				>
					<span
						className={`${styles.bubble} ${
							m.role === 'agent' ? styles.bubbleAgent : styles.bubbleUser
						}`}
					>
						{m.text}
					</span>
					{m.reaction ? (
						<span className={styles.reaction}>
							<ThumbsUp />
						</span>
					) : null}
				</div>
			))}
			<div className={styles.inputBar} aria-hidden="true">
				<span className={styles.inputAdd}>
					<svg
						className={styles.inputAddIcon}
						viewBox="0 0 14 14"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M7 1.5V12.5M1.5 7H12.5"
							stroke="#909093"
							strokeWidth="1.6"
							strokeLinecap="round"
						/>
					</svg>
				</span>
				<span className={styles.inputField} />
			</div>
		</div>
	</div>
);

export const MessageStates = () => (
	<Tabs
		orientation="vertical"
		className={styles.tabs}
		defaultSelectedKey="community"
	>
		<TabList aria-label="Agents" className={styles.tabList}>
			{AGENTS.map((agent) => (
				<Tab key={agent.id} id={agent.id} className={styles.tab}>
					<LandingImage
						src={agent.icon}
						alt=""
						className={styles.tabAvatar}
						width={64}
						height={64}
					/>
					<span className={styles.tabText}>
						<span className={styles.tabName}>{agent.name}</span>
						<span className={styles.tabRole}>{agent.role}</span>
					</span>
				</Tab>
			))}
		</TabList>
		{AGENTS.map((agent) => (
			<TabPanel key={agent.id} id={agent.id} className={styles.panel}>
				<ChatThread context={agent.context} messages={agent.messages} />
			</TabPanel>
		))}
	</Tabs>
);
